import { v4 as uuidv4 } from 'uuid'
import type { Rpts_ExhaustiveAnswer } from './Rpts.ExhaustiveAnswer'
import type { Serv_ServerRequestMapping, Serv_ServerResponseMapping } from '../Serv/Serv.types'
import type { Type_ICalendarEvent, Type_TValue } from '../Type/Type.hive'
import type { UUID } from '../../../shared/lib/typeHelpers.no-deps'
import type { ReportClientId } from '../../../shared/domain/reports/types.no-deps'
import type { FlowUnitIds } from '../../../shared/domain/flows/FlowUnitsIds.no-deps'

/**
 * Introducing a uuid to identify reports by
 * At the moment the "reportId" is an incrementing id generated by indexed db
 * And the "onlineId" is an incrementing id generated by the server database
 * Moving toward a uuid will let us avoid having to update the ids during the report lifecycle
 */
export type Rpts_ClientId = ReportClientId
export const Rpts_createClientId = () => uuidv4() as ReportClientId

export namespace NReports {
    export interface INewIntermediateReport extends TIntermediateReport {
        event: Type_ICalendarEvent
    }

    // TODO this will require significant refactoring -- potentially moving us towards being able to do
    // multiple reports at once.
    // The current logic is to convert a report into the intermediate structure, which is everything the same as
    // what will be sent to the server, apart from the fact that all the image arrays are numbers instead of strings.
    // This is due to the way that the sync logic is implemented.
    // Rather than refactor everything, I've created a type to handle that case which is a copy+paste of
    // TSubmittedReport, but with the images changed to strings. This is not ideal.
    export type TIntermediateReport = ({
        title?: string
        clientId: Rpts_ClientId
        parentClientId?: Rpts_ClientId
        location: number
        event: Type_ICalendarEvent
        status?: number
        coordinates: {
            lat: number
            lon: number
        }
        startDate: string
        endDate: string
        answers: Rpts_ExhaustiveAnswer[]
        subjectUnitId?: number
        subjectUnitVersion?: UUID
        flowUnitIds?: FlowUnitIds
    } & {
        reportedActionList: {
            actionList: number
            reportedActions: Array<{
                action: number
                reportResults: Array<
                    | ({
                          form: number
                          results: Array<{
                              question: number
                              answer: any
                          }>
                      } & {
                          type: 'location-form'
                          note?: string
                      })
                    | ({
                          form: number
                          results: Array<{
                              question: number
                              answer: any
                          }>
                      } & {
                          type: 'unit-form'
                          unit: number
                          note?: string
                      })
                    | ({
                          results: Array<{
                              issue: {
                                  id: number
                                  value?: {
                                      type: 'number'
                                      content: number
                                  }
                                  images?: number[]
                                  note?: string
                              }
                              fix?: {
                                  id: number
                                  images?: number[]
                                  note?: string
                              }
                              note?: string
                          }>
                      } & {
                          type: 'unit-issue'
                          unit: number
                      })
                    | ({
                          results: Array<{
                              issue: {
                                  id: number
                                  value?: {
                                      type: 'number'
                                      content: number
                                  }
                                  images?: number[]
                                  note?: string
                              }
                              fix?: {
                                  id: number
                                  images?: number[]
                                  note?: string
                              }
                              note?: string
                          }>
                      } & {
                          type: 'location-issue'
                      })
                >
            }>
        }
    }) & {
        reportId?: number
        uploaded?: boolean
        onlineId?: number
        scheduledInstance?: UUID
        teamDoneFor?: number
        workflowVersion: number
    }

    type TReportedItem = {
        images?: Array<string | number>
        note?: string
    }

    export type TReportedIssue = {
        id: number
        value: Type_TValue
    } & TReportedItem

    type TReportedFix = {
        id: number
    } & TReportedItem

    export type TReportedUnitForm = {
        type: 'unit-form'
        unit: number
        form: number
        note?: string
        results: Array<{
            question: number
            answer: any
        }>
    }

    export type TReportedLocationForm = {
        type: 'location-form'
        form: number
        note?: string
        results: Array<{
            question: number
            answer: any
        }>
    }

    export type TReportedUnitIssueResult = {
        issue: TReportedIssue
        fix: TReportedFix
        note?: string
    }

    export type TReportedUnitIssue = {
        type: 'unit-issue'
        unit: number
        results: TReportedUnitIssueResult[]
    }

    export type TReportedLocationIssue = {
        type: 'location-issue'
        results: TReportedUnitIssueResult[]
    }

    export type TReportResult = TReportedUnitForm | TReportedUnitIssue | TReportedLocationForm | TReportedLocationIssue

    export type TReportedAction = {
        action: number
        reportResults: TReportResult[]
    }

    export type TReportedActionList = {
        actionList: number
        reportedActions: TReportedAction[]
    }

    export function isSyncReport(report: TDatabaseReport): report is TSyncReport {
        return report.hasOwnProperty('date')
    }

    export function isUnuploadedReport(report: NReports.TDatabaseReport): report is NReports.TIntermediateReport {
        return report.uploaded === false
    }

    export type TSubmittedReport = TReportBase & Serv_ServerRequestMapping['v2.reports.add']

    export type TSyncReport = TReportBase & Serv_ServerResponseMapping['sync.v2']['result']['reports'][0]

    type TReportBase = {
        reportId?: number
        uploaded?: boolean
        onlineId?: number
        answers?: Rpts_ExhaustiveAnswer[]
    }

    export type TDatabaseReport = TSyncReport | TSubmittedReport | TIntermediateReport
}
